import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import FormAddressInput, { FormAddressInputProps } from './FormAddressInput';
import { FormAddressItemProps } from './FormAddressItem';
import Icons from '../../mon-compte/Icons';
import { Pays } from '../../../interfaces-base';
import { getCountryFlagUrl } from '../utils/form.util';
import './css/form-address-input-phone.css';

type ExtendedProps = Pick<FormAddressInputProps & FormAddressItemProps, 'label' | 'errorMessage' | 'loading' | 'name'>;
interface Props extends ExtendedProps {
  value: string|undefined;
  phoneType: 'mobile' | 'fixe';
  countries: Pays[];
  currentCountry: Pays;
  detectCountry?: Pays;
  onChange?: (value: string, country: Pays) => void;
}

const FormAddressInputPhone: FC<ExtendedProps & Props> = ({
  label,
  errorMessage,
  loading,
  name,
  value,
  phoneType,
  countries,
  currentCountry,
  detectCountry,
  onChange,
}) => {
  const [savedCountry, setSavedCountry] = useState<Pays|undefined>(undefined);
  const [selectOpen, setSelectOpen] = useState<boolean>(false);

  const canCloseRef = useRef<boolean>(true);

  useEffect(() => {
    setSavedCountry(detectCountry ?? savedCountry);
  }, [detectCountry]);

  const setCanClose = (v: boolean): void => {
    canCloseRef.current = v;
  };

  const getClearedValue = (v?: string): string|undefined => (
    savedCountry
      ? v?.replace(new RegExp(`^(\\+${savedCountry.phone_international_code})`, 'g'), '')
      : v
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const target = e.target;
    let tValue = target.value;
    const clearedValue = getClearedValue(tValue);
    if (savedCountry && clearedValue && clearedValue.length > 0 && clearedValue[0] !== '+') {
      tValue = `+${savedCountry.phone_international_code}${clearedValue}`;
    }
    onChange?.(tValue, savedCountry ?? currentCountry);
  };

  const handleClickCountry = (country: Pays): void => {
    const clearedValue = getClearedValue(value);
    if (clearedValue && clearedValue.length > 0 && clearedValue[0] !== '+') {
      onChange?.(`+${country.phone_international_code}${clearedValue}`, country);
    }

    setSavedCountry(country);
    setSelectOpen(false);
    setCanClose(true);
  };

  const countryActif = savedCountry ?? currentCountry;
  const prependItem = (
    <div className="fa-phone-prepend">
      <div className="fa-ic-phone">
        <Icons icon={phoneType === 'mobile' ? 'ic-mobile-phone' : 'ic-phone'} fill="#888" width="24" height="24" />
      </div>
      <div className="fa-phone-country-select-main" onBlur={() => canCloseRef.current && setSelectOpen(false)}>
        <button className="fa-phone-country-select" onClick={() => setSelectOpen(!selectOpen)}>
          <img className="fa-country-flag" src={getCountryFlagUrl(countryActif)} width="24" height="24" />
          <span>+{countryActif.phone_international_code}</span>
          <Icons icon="arrow_drop_down" size={18} />
        </button>
        <div
          className={`fa-phone-num-list ${selectOpen && 'open'}`}
          onMouseEnter={() => setCanClose(false)}
          onMouseLeave={() => setCanClose(true)}
          onMouseDown={() => setCanClose(false)}
          onMouseUp={() => setCanClose(true)}
        >
          {countries.map(item => (
            <button
              key={`fa-phone-num-item-${item.idPays}`}
              className={`${item.idPays === countryActif.idPays && 'selected'}`}
              onClick={() => handleClickCountry(item)}
            >
              <img src={getCountryFlagUrl(item)} width="24" height="24"  />
              <span>+{item.phone_international_code}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <FormAddressInput
      label={label}
      prependItem={prependItem}
      errorMessage={errorMessage}
      loading={loading}
      value={getClearedValue(value)}
      name={name}
      onChange={handleChange}
    />
  );
};

export default FormAddressInputPhone;
